/**
 * Steam (Phase 8 — the first of the service environmental physics).
 *
 * Sabotage the boiler and it doesn't just switch off: it vents. Steam pours out
 * of the plant and every riser tap on the floor, spreads along the open floor,
 * and blocks line of sight for EVERYONE — guards, cameras, and you.
 *
 * That symmetry is the point. Unlike darkness (which night vision beats) or
 * cover (which only helps if you're behind it), steam is a wall you can move
 * and nobody can see through, including the person who made it. It's a way to
 * cross a room you have no business crossing, at the price of not being able to
 * see what you're crossing into.
 *
 * A plain scalar field with diffusion + decay — no fluid dynamics. What matters
 * is that it billows out, fills a space, and fades once the source stops.
 *
 * Pure + headless (rule 4).
 */

import { CONFIG } from '../config';
import { isBlocking } from './tiles';
import { tileAt, type MapData } from './map';

/** Below this a tile counts as dry. It must stay well under a single tick's
 *  transfer between neighbours, or the denoise silently eats the spread and the
 *  steam never leaves the vent. */
const EPS = 1e-4;

export class SteamField {
  readonly level: Float32Array;
  private readonly next: Float32Array;

  constructor(readonly width: number, readonly height: number) {
    this.level = new Float32Array(width * height);
    this.next = new Float32Array(width * height);
  }

  /** Wipe the field (floor change, or the boiler being fixed). */
  reset(): void {
    this.level.fill(0);
  }

  /** Is there anything at all to simulate? Keeps the tick free when dry. */
  get active(): boolean {
    for (let i = 0; i < this.level.length; i++) if (this.level[i]! > EPS) return true;
    return false;
  }

  at(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return 0;
    return this.level[y * this.width + x] ?? 0;
  }

  /** Thick enough to hide behind — the threshold sight checks use. */
  opaqueAt(x: number, y: number): boolean {
    return this.at(x, y) >= CONFIG.steam.opaqueAt;
  }

  /** Vent steam into a tile (a plant, a riser tap). */
  emit(x: number, y: number, amount: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    const i = y * this.width + x;
    this.level[i] = Math.min(CONFIG.steam.max, (this.level[i] ?? 0) + amount);
  }

  /**
   * Diffuse into open neighbours and fade. Walls hold it in, which is what makes
   * a filled room read as a filled ROOM rather than a haze over the floor.
   */
  spread(map: MapData, dt: number): void {
    const { width, height, level, next } = this;
    const cfg = CONFIG.steam;
    const flow = Math.min(0.9, cfg.spreadRate * dt);
    const keep = Math.max(0, 1 - cfg.decayRate * dt);
    next.set(level);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = y * width + x;
        const here = level[i] ?? 0;
        if (here <= EPS) continue;
        if (isBlocking(tileAt(map, x, y))) { next[i] = 0; continue; }
        const open: number[] = [];
        if (x > 0 && !isBlocking(tileAt(map, x - 1, y))) open.push(i - 1);
        if (x < width - 1 && !isBlocking(tileAt(map, x + 1, y))) open.push(i + 1);
        if (y > 0 && !isBlocking(tileAt(map, x, y - 1))) open.push(i - width);
        if (y < height - 1 && !isBlocking(tileAt(map, x, y + 1))) open.push(i + width);
        if (open.length === 0) continue;
        // Only the excess over a neighbour moves, so it evens out rather than
        // sloshing back and forth.
        for (const j of open) {
          const diff = here - (level[j] ?? 0);
          if (diff <= 0) continue;
          const move = (diff * flow) / open.length;
          next[i] = (next[i] ?? 0) - move;
          next[j] = (next[j] ?? 0) + move;
        }
      }
    }
    for (let i = 0; i < level.length; i++) {
      const v = (next[i] ?? 0) * keep;
      level[i] = v < EPS ? 0 : Math.min(CONFIG.steam.max, v);
    }
  }
}
